'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import { Text } from '../../../components/styled/text';
import { renderHttpMethodText } from '../../../utils/http-badge.utils';
import { getUrlPath } from '../../../utils/url-path.utils';
import { ToolHarCredentialBadge } from './ToolHarCredentialBadge';
import { HarEntryRequestResponseEditors } from '../../har/HarEntryRequestResponseEditors';
import { useEnrichedToolHarEntry } from '../../har/useEnrichedToolHarEntry';

type ToolHarRequestResponseProps = {
  har: ToolExecutionHar;
  hideHarSummaryRow?: boolean;
};

export function ToolHarRequestResponse({
  har,
  hideHarSummaryRow,
}: ToolHarRequestResponseProps) {
  const { harEntry } = useEnrichedToolHarEntry({ har });
  const request = har.harEntry.request;
  const response = har.harEntry.response;
  const showSummaryRow = !hideHarSummaryRow;

  return (
    <VStack alignItems="stretch" gap={0} width="100%" h="400px">
      {showSummaryRow ? (
        <HStack
          alignItems="center"
          gap={1.5}
          px={2}
          py={1.5}
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.subtle"
          flexShrink={0}
          width="100%"
          justifyContent="space-between"
        >
          <HStack gap={1.5} flex="1" minW={0}>
            {renderHttpMethodText(request.method)}
            <Text
              fontSize="xs"
              fontFamily="mono"
              truncate
              flex="1"
              minW={0}
              title={request.url}
            >
              {getUrlPath(request.url)}
            </Text>
            {renderHttpMethodText(response.status)}
          </HStack>
          <ToolHarCredentialBadge har={har} />
        </HStack>
      ) : null}
      <Box flex="1" minH={0}>
        <HarEntryRequestResponseEditors
          harEntry={harEntry}
          viewResetKey={har.id}
          height="100%"
          requestTitle="Request"
          responseTitle="Response"
          responseStatusCode={response.status}
          canonicalRequestUrl={request.url}
        />
      </Box>
    </VStack>
  );
}
